import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  TrendingDown, AlertTriangle, Clock, XCircle, Activity, 
  Pill, Utensils, Leaf, User, RefreshCw, ChevronRight,
  Zap, Brain, BarChart3, Lightbulb
} from 'lucide-react';

const API = '/api';

const EVENT_TYPE_CONFIG = {
  lifeflo:    { icon: Leaf,     color: 'text-emerald-400', bg: 'bg-emerald-500/10', label: 'LifeFlō' },
  regenflo:   { icon: Activity, color: 'text-blue-400',    bg: 'bg-blue-500/10',    label: 'ReGenflō' },
  medication: { icon: Pill,     color: 'text-amber-400',   bg: 'bg-amber-500/10',   label: 'Medication' },
  food:       { icon: Utensils, color: 'text-rose-400',    bg: 'bg-rose-500/10',    label: 'Food' },
};

const BLOCK_LABELS = {
  wake: 'Wake',
  morning: 'Morning',
  mid_morning: 'Mid-Morning',
  lunch: 'Lunch',
  afternoon: 'Afternoon',
  evening: 'Evening',
  dinner: 'Dinner',
  night: 'Night',
  sleep: 'Sleep'
};

// Generate behavior pattern insights from friction data
function generateInsights(data) {
  if (!data || data.total_logs === 0) return [];
  
  const insights = [];
  
  // Most unstable block insight
  if (data.unstable_blocks?.length > 0) {
    const worst = data.unstable_blocks[0];
    if (worst.instability_rate > 30) {
      insights.push({
        type: 'critical',
        icon: AlertTriangle,
        message: `${BLOCK_LABELS[worst.block] || worst.block} block shows highest instability (${worst.instability_rate}% deviation rate)`,
        detail: `${worst.skipped} skipped, ${worst.changed} changed out of ${worst.total} events`
      });
    } else if (worst.instability_rate > 15) {
      insights.push({
        type: 'warning',
        icon: TrendingDown,
        message: `${BLOCK_LABELS[worst.block] || worst.block} block needs attention (${worst.instability_rate}% deviation)`,
        detail: `Consider simplifying this time block`
      });
    }
  }
  
  // Most skipped item insight
  if (data.most_skipped?.length > 0) {
    const worst = data.most_skipped[0];
    if (worst.skip_rate > 20) {
      insights.push({
        type: 'critical',
        icon: XCircle,
        message: `"${worst.name || worst.code}" is frequently skipped (${worst.skip_rate}%)`,
        detail: `${worst.skip_count} skips — consider reviewing timing or necessity`
      });
    }
  }
  
  // Type-level pattern
  const typeStats = {};
  data.most_skipped?.forEach(item => {
    const t = item.event_type || 'unknown';
    typeStats[t] = (typeStats[t] || 0) + (item.skip_count || 0);
  });
  data.most_changed?.forEach(item => {
    const t = item.event_type || 'unknown';
    typeStats[t] = (typeStats[t] || 0) + (item.change_count || 0);
  });
  
  const typeEntries = Object.entries(typeStats).sort((a, b) => b[1] - a[1]);
  if (typeEntries.length > 0 && typeEntries[0][1] > 3) {
    const [type, count] = typeEntries[0];
    const config = EVENT_TYPE_CONFIG[type] || { label: type };
    insights.push({
      type: 'info',
      icon: Brain,
      message: `${config.label} events show most friction (${count} deviations)`,
      detail: `This category may need schedule optimization`
    });
  }
  
  // Timing pattern
  if (data.most_delayed?.length > 0) {
    const avgDelay = data.most_delayed.reduce((sum, i) => sum + (i.avg_delay_min || 0), 0) / data.most_delayed.length;
    if (avgDelay > 20) {
      insights.push({
        type: 'warning',
        icon: Clock,
        message: `Average timing delay is ${Math.round(avgDelay)} minutes`,
        detail: `Morning and post-lunch blocks typically show highest delays`
      });
    }
  }
  
  // Morning vs evening comparison
  const morningBlocks = data.unstable_blocks?.filter(b => ['wake', 'morning', 'mid_morning'].includes(b.block)) || [];
  const eveningBlocks = data.unstable_blocks?.filter(b => ['evening', 'dinner', 'night', 'sleep'].includes(b.block)) || [];
  
  const morningAvg = morningBlocks.length > 0 ? morningBlocks.reduce((s, b) => s + b.instability_rate, 0) / morningBlocks.length : 0;
  const eveningAvg = eveningBlocks.length > 0 ? eveningBlocks.reduce((s, b) => s + b.instability_rate, 0) / eveningBlocks.length : 0;
  
  if (morningAvg > 0 && eveningAvg > 0) {
    if (morningAvg > eveningAvg + 10) {
      insights.push({
        type: 'info',
        icon: Lightbulb,
        message: `Morning adherence is weaker than evening`,
        detail: `Morning: ${Math.round(morningAvg)}% deviation vs Evening: ${Math.round(eveningAvg)}%`
      });
    } else if (eveningAvg > morningAvg + 10) {
      insights.push({
        type: 'info',
        icon: Lightbulb,
        message: `Evening adherence is weaker than morning`,
        detail: `Evening: ${Math.round(eveningAvg)}% deviation vs Morning: ${Math.round(morningAvg)}%`
      });
    }
  }
  
  return insights;
}

function InsightCard({ insight }) {
  const Icon = insight.icon;
  const bgColor = insight.type === 'critical' ? 'bg-red-500/10 border-red-500/20' : 
                  insight.type === 'warning' ? 'bg-amber-500/10 border-amber-500/20' : 
                  'bg-blue-500/10 border-blue-500/20';
  const iconColor = insight.type === 'critical' ? 'text-red-400' : 
                    insight.type === 'warning' ? 'text-amber-400' : 
                    'text-blue-400';
  
  return (
    <div className={`rounded-lg border p-4 ${bgColor}`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 ${iconColor} flex-shrink-0 mt-0.5`} />
        <div>
          <div className="text-sm text-white/90">{insight.message}</div>
          {insight.detail && <div className="text-xs text-white/50 mt-1">{insight.detail}</div>}
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, subtitle, icon: Icon, color, trend }) {
  return (
    <Card className="bg-adm-surface/[0.02] border-white/[0.06]">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs text-white/40">{title}</div>
            <div className={`text-2xl font-bold ${color} mt-1`}>{value}</div>
            {subtitle && <div className="text-[10px] text-white/30 mt-1">{subtitle}</div>}
          </div>
          <div className={`w-10 h-10 rounded-lg ${color.replace('text-', 'bg-').replace('400', '500/10')} flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${color}`} />
          </div>
        </div>
        {trend && (
          <div className={`text-[10px] mt-2 ${trend > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
            {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}% from last week
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function FrictionItem({ item, type }) {
  const typeConfig = EVENT_TYPE_CONFIG[item.event_type] || EVENT_TYPE_CONFIG.regenflo;
  const Icon = typeConfig.icon;
  
  const value = type === 'skipped' ? item.skip_rate : type === 'changed' ? item.change_rate : item.avg_delay_min;
  const count = type === 'skipped' ? item.skip_count : type === 'changed' ? item.change_count : item.delay_count;
  const unit = type === 'delayed' ? 'min avg' : '%';
  
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/[0.04] last:border-0">
      <div className="flex items-center gap-2">
        <div className={`w-6 h-6 rounded ${typeConfig.bg} flex items-center justify-center`}>
          <Icon className={`w-3.5 h-3.5 ${typeConfig.color}`} />
        </div>
        <div>
          <div className="text-xs text-white/80">{item.name || item.code}</div>
          <div className="text-[10px] text-white/30 font-mono">{item.code}</div>
        </div>
      </div>
      <div className="text-right">
        <div className={`text-sm font-semibold ${type === 'skipped' ? 'text-red-400' : type === 'changed' ? 'text-amber-400' : 'text-blue-400'}`}>
          {value}{unit}
        </div>
        <div className="text-[10px] text-white/30">{count} {type === 'delayed' ? 'delays' : 'times'}</div>
      </div>
    </div>
  );
}

function BlockBar({ block }) {
  const completionRate = 100 - block.instability_rate;
  const barColor = block.instability_rate > 30 ? 'bg-red-500' : block.instability_rate > 15 ? 'bg-amber-500' : 'bg-emerald-500';
  
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="w-24 text-xs text-white/50">{BLOCK_LABELS[block.block] || block.block}</div>
      <div className="flex-1 h-2 bg-adm-surface/5 rounded-full overflow-hidden">
        <div className={`h-full ${barColor}`} style={{ width: `${completionRate}%` }} />
      </div>
      <div className={`w-12 text-right text-xs ${block.instability_rate > 20 ? 'text-red-400' : 'text-white/50'}`}>
        {block.instability_rate}%
      </div>
    </div>
  );
}

export default function ExecutionIntelligence() {
  const { user } = useAuth();
  const [phases, setPhases] = useState([]);
  const [selectedPhase, setSelectedPhase] = useState('');
  const [period, setPeriod] = useState('14');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const token = localStorage.getItem('rf_access_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  
  // Fetch phases
  useEffect(() => {
    fetch(`${API}/admin/v3/phases?status=active`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.phases) {
          setPhases(d.phases);
          if (d.phases.length > 0) setSelectedPhase(d.phases[0].id);
        }
      });
  }, []);
  
  // Fetch friction data
  const fetchFriction = useCallback(async () => {
    if (!selectedPhase) return;
    setLoading(true);
    
    const res = await fetch(
      `${API}/admin/v3/phases/${selectedPhase}/friction-analysis?days=${period}`, 
      { headers }
    );
    if (res.ok) {
      setData(await res.json());
    }
    setLoading(false);
  }, [selectedPhase, period]);
  
  useEffect(() => {
    fetchFriction();
  }, [fetchFriction]);
  
  const insights = data ? generateInsights(data) : [];
  
  return (
    <AdminLayout>
      <div className="p-6 max-w-[1400px] mx-auto" data-testid="execution-intelligence">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-white/90">Execution Intelligence</h1>
            <p className="text-xs text-white/40 mt-1">Adherence patterns, friction zones, and protocol execution insights</p>
            <div className="flex items-center gap-3 mt-2 text-[8px] text-white/15">
              <span>Role: Admin / Counselor</span>
              <span>Data: daily_execution_logs, phase_schedule_items</span>
              <span>Purpose: Identify where adherence is breaking, which time blocks are weak, recurring skips</span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={selectedPhase} onValueChange={setSelectedPhase}>
              <SelectTrigger className="w-[200px] bg-adm-surface/5 border-white/10 text-white h-9 text-xs">
                <SelectValue placeholder="Select phase" />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0f] border-white/10">
                {phases.map(p => (
                  <SelectItem key={p.id} value={p.id} className="text-white text-xs">{p.phase_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[120px] bg-adm-surface/5 border-white/10 text-white h-9 text-xs">
                <SelectValue placeholder="Period" />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0f] border-white/10">
                <SelectItem value="7" className="text-white text-xs">Last 7 days</SelectItem>
                <SelectItem value="14" className="text-white text-xs">Last 14 days</SelectItem>
                <SelectItem value="30" className="text-white text-xs">Last 30 days</SelectItem>
              </SelectContent>
            </Select>
            
            <Button onClick={fetchFriction} size="sm" variant="ghost" className="text-xs text-white/50 h-9">
              <RefreshCw className="w-3 h-3 mr-1" /> Refresh
            </Button>
          </div>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center h-64 text-white/40">Loading intelligence data...</div>
        ) : !data || data.total_logs === 0 ? (
          <div className="text-center py-12 text-white/40">
            <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-20" />
            <p>No execution data available for analysis</p>
            <p className="text-xs mt-2">Execution logs are needed to generate insights</p>
          </div>
        ) : (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-5 gap-4 mb-6">
              <StatCard 
                title="Total Events Logged" 
                value={data.total_logs} 
                icon={Activity} 
                color="text-white/70"
              />
              <StatCard 
                title="Total Skips" 
                value={data.summary?.total_skips || 0} 
                subtitle={`${Math.round((data.summary?.total_skips || 0) / data.total_logs * 100)}% of events`}
                icon={XCircle} 
                color="text-red-400"
              />
              <StatCard 
                title="Total Changes" 
                value={data.summary?.total_changes || 0} 
                subtitle={`${Math.round((data.summary?.total_changes || 0) / data.total_logs * 100)}% of events`}
                icon={AlertTriangle} 
                color="text-amber-400"
              />
              <StatCard 
                title="On-Time Execution" 
                value={data.summary?.total_on_time || 0} 
                subtitle={`${Math.round((data.summary?.total_on_time || 0) / data.total_logs * 100)}% adherence`}
                icon={Clock} 
                color="text-emerald-400"
              />
              <StatCard 
                title="Analysis Period" 
                value={`${data.period_days}d`} 
                icon={BarChart3} 
                color="text-blue-400"
              />
            </div>
            
            {/* Behavior Pattern Insights */}
            {insights.length > 0 && (
              <div className="mb-6">
                <h2 className="text-sm font-medium text-white/70 mb-3 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-amber-400" />
                  Behavior Pattern Insights
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  {insights.map((insight, i) => (
                    <InsightCard key={i} insight={insight} />
                  ))}
                </div>
              </div>
            )}
            
            {/* Main Grid */}
            <div className="grid grid-cols-3 gap-4">
              {/* Most Skipped */}
              <Card className="bg-adm-surface/[0.02] border-white/[0.06]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-white/70 flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-400" />
                    Most Skipped Events
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {data.most_skipped?.length > 0 ? (
                    data.most_skipped.slice(0, 5).map((item, i) => (
                      <FrictionItem key={i} item={item} type="skipped" />
                    ))
                  ) : (
                    <div className="text-xs text-white/30 text-center py-4">No skipped events</div>
                  )}
                </CardContent>
              </Card>
              
              {/* Most Changed */}
              <Card className="bg-adm-surface/[0.02] border-white/[0.06]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-white/70 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                    Most Changed Events
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {data.most_changed?.length > 0 ? (
                    data.most_changed.slice(0, 5).map((item, i) => (
                      <FrictionItem key={i} item={item} type="changed" />
                    ))
                  ) : (
                    <div className="text-xs text-white/30 text-center py-4">No changed events</div>
                  )}
                </CardContent>
              </Card>
              
              {/* Most Delayed */}
              <Card className="bg-adm-surface/[0.02] border-white/[0.06]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-white/70 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-400" />
                    Most Delayed Events
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {data.most_delayed?.length > 0 ? (
                    data.most_delayed.slice(0, 5).map((item, i) => (
                      <FrictionItem key={i} item={item} type="delayed" />
                    ))
                  ) : (
                    <div className="text-xs text-white/30 text-center py-4">No delayed events</div>
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* Block Stability */}
            <Card className="bg-adm-surface/[0.02] border-white/[0.06] mt-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-white/70 flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-rose-400" />
                  Time Block Stability (Deviation Rate)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.unstable_blocks?.length > 0 ? (
                  data.unstable_blocks.map((block, i) => (
                    <BlockBar key={i} block={block} />
                  ))
                ) : (
                  <div className="text-xs text-white/30 text-center py-4">No block data available</div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
